import { IAccount, ICategory, Category, IThirdPartyInvoice } from '$models'
import {
  thirdPartyAccountService,
  categoryService,
  thirdPartyContactService,
  stockReceivedService,
  supplierStatementService,
} from '$services'
import {
  enqueueLoadedSnackbar,
  exceptionMessageHandler,
  getActiveCompanySettings,
} from '$utils'
import { SharedSlice } from 'src/components/slice'
import { IThirdPartyContact } from 'src/models/interfaces/stock/thirdPartyContact'
import { StateCreator } from 'zustand'
import { AccountingSystemExportSlice } from '../accounting-system-export/slice'
import { CombineOption } from '../_common/helpers/combineOption'
import { ExportType } from '../_common/helpers/exportType'
import { AccountingSystem } from '$constants'

interface ExportToState {
  categories: ICategory[]
  thirdPartyAccounts: IAccount[]
  thirdPartyContacts: IThirdPartyContact[]
  exportResults: IThirdPartyInvoice[]
}

export interface ExportToSlice extends ExportToState {
  resetExportToSlice: () => void

  setOptions: (options: {
    combine: CombineOption
    accountingSystem: AccountingSystem
    exportType: ExportType
    supplierIds: string[]
    supplierStatementIds: string[]
    invoicesIds: string[]
    purchaseOrdersIds: string[]
  }) => void

  setExportResults: (exportResults: IThirdPartyInvoice[]) => void

  /**
   * Get all categories
   */
  getCategories: () => Promise<void>

  /**
   * Get all (xero) third party accounts
   * @returns all third party accounts
   */
  getThirdPartyAccounts: (system: string) => Promise<void>

  /**
   * Get all (xero) third party contacts
   * @returns all third party contacts
   */
  getThirdPartyContacts: (system: string) => Promise<void>

  /**
   * Export to accounting system (eg. Xero)
   * @param successMessage Success Message to display if the export was successful
   * @param errorMessage Error Message to display if the export has failed
   */
  exportToAccountingSystem: (
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>
}

const initialState: ExportToState = {
  categories: [],
  thirdPartyAccounts: [],
  thirdPartyContacts: [],
  exportResults: [],
}

const createExportToSlice: StateCreator<
  ExportToSlice & SharedSlice & AccountingSystemExportSlice,
  [],
  [],
  ExportToSlice
> = (set, get) => ({
  ...initialState,

  resetExportToSlice: () => set(initialState),

  setExportResults: (exportResults: IThirdPartyInvoice[]): void => {
    set(() => ({
      exportResults: exportResults,
    }))
  },

  setOptions: (options: {
    combine: CombineOption
    accountingSystem: AccountingSystem
    exportType: ExportType
    supplierIds: string[]
    supplierStatementIds: string[]
    invoicesIds: string[]
    purchaseOrdersIds: string[]
  }) => {
    set(() => ({
      combine: options.combine,
      accountingSystemOption: options.accountingSystem,
      exportType: options.exportType,
      suppliersIdsToExport: options.supplierIds,
      supplierStatementsIdsToExport: options.supplierStatementIds,
      invoicesIdsToExport: options.invoicesIds,
      purchaseOrdersIdsToExport: options.purchaseOrdersIds,
    }))
  },

  getCategories: async (): Promise<void> => {
    try {
      const categories: ICategory[] = await categoryService.getAllCategories()
      const { xeroFreightAccountPreference } = getActiveCompanySettings()

      // add the Freight to categories so we can display it using the same logic as the rest of the categories
      const freightCategory: ICategory[] = [
        new Category({
          id: 'freight',
          name: 'Freight',
          externalAccountCode: xeroFreightAccountPreference ?? undefined,
        }),
      ]

      const result = categories.concat(freightCategory)

      set(() => ({
        categories: result,
      }))
    } catch (error) {
      set(() => ({
        categories: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve categories')
    }
  },

  getThirdPartyAccounts: async (system: string): Promise<void> => {
    try {
      const thirdPartyAccounts =
        await thirdPartyAccountService.getThirdPartyAccounts(system)
      set(() => ({
        thirdPartyAccounts,
      }))
    } catch (error) {
      set(() => ({
        thirdPartyAccounts: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve third party accounts')
    }
  },

  getThirdPartyContacts: async (system: string): Promise<void> => {
    try {
      const thirdPartyContacts =
        await thirdPartyContactService.getThirdPartyContacts(system)
      set(() => ({
        thirdPartyContacts,
      }))
    } catch (error) {
      set(() => ({
        thirdPartyContacts: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve third party contacts')
    }
  },

  exportToAccountingSystem: async (
    successMessage?: string,
    errorMessage?: string
  ): Promise<void> => {
    try {
      const {
        exportType,
        combineOption,
        invoicesIdsToExport,
        purchaseOrdersIdsToExport,
        supplierStatementsIdsToExport,
      } = get()

      let thirdPartyInvoices: IThirdPartyInvoice[] = []
      const combine = combineOption === 'combined'
      if (exportType === 'statements') {
        thirdPartyInvoices =
          await supplierStatementService.exportToAccountingSystem(
            combine,
            supplierStatementsIdsToExport
          )
      } else if (exportType === 'orders') {
        thirdPartyInvoices =
          await stockReceivedService.exportToAccountingSystem(
            combine,
            invoicesIdsToExport,
            purchaseOrdersIdsToExport
          )
      }

      enqueueLoadedSnackbar(successMessage, 'success')
      set(() => ({
        exportResults: thirdPartyInvoices,
      }))
    } catch (error) {
      exceptionMessageHandler(
        error,
        errorMessage ?? 'Failed to export to accounting system'
      )
    }
  },
})

export default createExportToSlice
